import { api, apiFetch, snakeToCamel, camelToSnake } from '@/core/api/client'

import type { ContactStatus, ContactStage } from '../stores'

export interface PBContact {
  id: string
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  position: string
  status: ContactStatus
  stage: ContactStage
  source: string
  coursesInterested: string[]
  lastContact: string
  notes: string
  assignedTo: string | null
  assignedToName: string | null
  notesHistory: PBContactNote[]
  pipelineLogs: PBContactPipelineLog[]
  createdAt: string
  updatedAt: string
}

export interface PBContactNote {
  id: string
  contact: string
  noteType: string
  content: string
  createdBy: string | null
  createdByName: string | null
  createdAt: string
}

export interface PBContactPipelineLog {
  id: string
  stepType: string
  description: string
  metadata: Record<string, unknown>
  createdAt: string
}

const contactsCollection = api.collection<PBContact>('contacts')

/** Fetch all CRM contacts. */
export async function getAllContacts(): Promise<PBContact[]> {
  return contactsCollection.getFullList({ sort: '-created_at' })
}

/** Fetch a single contact by ID. */
export async function getContact(id: string): Promise<PBContact> {
  return contactsCollection.getOne(id)
}

/** Create a new contact. */
export async function createContact(data: {
  firstName: string
  lastName: string
  email: string
  phone: string
  company: string
  position: string
  status: ContactStatus
  stage: ContactStage
  source?: string
  coursesInterested: string[]
  lastContact: string
  notes: string
  assignedTo?: string | null
}): Promise<PBContact> {
  return contactsCollection.create(data)
}

/** Update an existing contact. */
export async function updateContact(
  id: string,
  data: Partial<{
    firstName: string
    lastName: string
    email: string
    phone: string
    company: string
    position: string
    status: ContactStatus
    stage: ContactStage
    source: string
    coursesInterested: string[]
    lastContact: string
    notes: string
    assignedTo: string | null
  }>
): Promise<PBContact> {
  return contactsCollection.update(id, data)
}

/** Delete a contact. */
export async function deleteContact(id: string): Promise<void> {
  await contactsCollection.delete(id)
}

/** Add a note/activity to a contact. */
export async function addContactNote(
  contactId: string,
  data: { noteType: string; content: string }
): Promise<PBContactNote> {
  const res = await apiFetch(`/contacts/${contactId}/notes/`, {
    method: 'POST',
    body: JSON.stringify(camelToSnake(data)),
  })
  if (!res.ok) {throw new Error('Failed to add note')}
  return snakeToCamel<PBContactNote>(await res.json())
}

/** Generate an AI-powered email draft for a contact. */
export async function draftContactEmail(
  contactId: string,
  data: { purpose?: string; tone?: string } = {}
): Promise<{ subject: string; body: string; contact_name: string; contact_email: string }> {
  const res = await apiFetch(`/contacts/${contactId}/draft-email/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || 'Failed to generate email draft')
  }
  return res.json()
}

/** Send an email to a contact from the CRM. */
export async function sendContactEmail(
  contactId: string,
  data: { subject: string; message: string }
): Promise<{ success: boolean; message?: string }> {
  const res = await apiFetch(`/contacts/${contactId}/send-email/`, {
    method: 'POST',
    body: JSON.stringify(data),
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({}))
    throw new Error(err.error || 'Failed to send email')
  }
  return res.json()
}

/** Update just the stage of a contact (quick action). */
export async function updateContactStage(
  id: string,
  stage: ContactStage
): Promise<PBContact> {
  return updateContact(id, { stage })
}

